import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { fmtAmount } from '../utils/numberFormat';
import { pageTitle } from '../utils/pageTitles';
import { localeOf } from '../i18n';
import ForecastWidget from '../components/ForecastWidget';
import PurchaseInsightsWidget from '../components/PurchaseInsightsWidget';

function DashboardContent({ onNavigate }) {
  const { user, company, hasPermission } = useAuth();
  const { t } = useTranslation();
  // role/plan kodlarını sözlükten çevir — key yoksa orijinal kod döner
  const roleName = (r) => r ? t('role.' + r, { defaultValue: r }) : '';
  const planName = (p) => p ? t('plan.' + p, { defaultValue: p }) : '';
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString(localeOf(), { day:'2-digit', month:'long', year:'numeric', hour:'2-digit', minute:'2-digit' }) : '-';

  return (
    <div className="dashboard-content">
      <div className="dashboard-header">
        <h1>{t('dashboard.welcome')}, <span>{user?.fullName?.split(' ')[0]}</span></h1>
        <p>{company?.name} - {planName(company?.plan)} {t('dashboard.package')}</p>
      </div>

      <div className="dashboard-grid">
        <div className="dash-card">
          <div className="dash-card-icon purple">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
          </div>
          <div className="dash-card-label">{t('dashboard.role')}</div>
          <div className="dash-card-value">{roleName(user?.role)}</div>
          <div className="dash-card-sub">{user?.email}</div>
        </div>
        <div className="dash-card">
          <div className="dash-card-icon green">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"/></svg>
          </div>
          <div className="dash-card-label">{t('dashboard.company')}</div>
          <div className="dash-card-value">{company?.code}</div>
          <div className="dash-card-sub">{company?.name}</div>
        </div>
        <div className="dash-card">
          <div className="dash-card-icon blue">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
          </div>
          <div className="dash-card-label">{t('dashboard.lastLogin')}</div>
          <div className="dash-card-value" style={{fontSize:'0.82rem'}}>{fmtDate(user?.lastLogin)}</div>
          <div className="dash-card-sub">{t('dashboard.loginCount')}: {user?.loginCount || 0}</div>
        </div>
        <div className="dash-card">
          <div className="dash-card-icon amber">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          </div>
          <div className="dash-card-label">{t('dashboard.plan')}</div>
          <div className="dash-card-value">{planName(company?.plan)}</div>
          <div className="dash-card-sub">{t('dashboard.maxUsersLabel', { count: company?.maxUsers || 0 })}</div>
        </div>
      </div>

      {/* Widget: Bugünkü Satış */}
      {hasPermission('invoices.view') && <TodaySalesWidget onNavigate={onNavigate} />}

      {/* Widget: CRM Pipeline Forecast */}
      {hasPermission('prospect_accounts.view') && <ForecastWidget onNavigate={onNavigate} />}

      {/* Widget: Satın Alma Özeti */}
      {hasPermission('invoices.view') && <PurchaseInsightsWidget onNavigate={onNavigate} />}

      {/* Widget: Anlık Aktif Kullanıcılar */}
      <ActiveUsersWidget currentUserId={user?.id} />

    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// AKTIF KULLANICILAR (son 5 dakika içinde API isteği yapanlar)
// ─────────────────────────────────────────────────────────────────────────────
function ActiveUsersWidget({ currentUserId }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const r = await api.get('/dashboard/active-users', { params: { minutes: 5 } });
        if (!cancelled && r.data.success) setUsers(r.data.data || []);
      } catch (_) {}
      if (!cancelled) setLoading(false);
    };
    run();
    const iv = setInterval(() => { if (!document.hidden) run(); }, 20000);
    return () => { cancelled = true; clearInterval(iv); };
  }, []);

  const ago = (s) => {
    if (s < 60) return 'şimdi';
    if (s < 600) return Math.floor(s/60) + ' dk önce';
    return Math.floor(s/60) + ' dk önce';
  };
  const initials = (n) => (n || '?').split(' ').filter(Boolean).slice(0,2).map(w => w[0]).join('').toUpperCase() || '?';

  return (
    <div style={{ background:'var(--bg-card)', border:'1px solid var(--border-secondary)', borderRadius: 12, padding:'14px 18px', marginBottom: 12 }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: 10, flexWrap:'wrap', gap: 8 }}>
        <div>
          <h3 style={{ margin: 0, fontSize:'0.85rem', fontWeight: 600, color:'var(--text-secondary)', textTransform:'uppercase', letterSpacing:'0.04em' }}>
            🟢 Şu An Aktif Kullanıcılar
          </h3>
          <div style={{ fontSize:'0.72rem', color:'var(--text-tertiary)', marginTop: 2 }}>
            Son 5 dakika içinde sistemde işlem yapanlar
          </div>
        </div>
        <div style={{ fontSize:'0.78rem', fontWeight: 700, color:'var(--success)', padding:'4px 12px', borderRadius: 100, background:'rgba(16,185,129,0.10)', border:'1px solid rgba(16,185,129,0.25)' }}>
          {loading ? '…' : users.length}
        </div>
      </div>
      {loading ? (
        <div style={{ padding: 10, color:'var(--text-tertiary)', fontSize:'0.85rem' }}>Yükleniyor...</div>
      ) : users.length === 0 ? (
        <div style={{ padding: 10, color:'var(--text-tertiary)', fontSize:'0.85rem' }}>Şu an sistemde sadece sen varsın.</div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap: 6 }}>
          {users.map(u => {
            const isMe = u.id === currentUserId;
            const sec = u.seconds_ago || 0;
            const pulseColor = sec < 60 ? '#16a34a' : sec < 180 ? '#84cc16' : '#f59e0b';
            return (
              <div key={u.id} style={{
                display:'flex', alignItems:'center', gap: 10, padding:'8px 10px',
                background: isMe ? 'rgba(59,130,246,0.06)' : 'var(--bg-secondary)',
                border: '1px solid ' + (isMe ? 'rgba(59,130,246,0.25)' : 'var(--border-secondary)'),
                borderRadius: 8,
              }}>
                <div style={{ position:'relative' }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: '50%',
                    background: u.image_url ? `center/cover no-repeat url(${u.image_url})` : 'linear-gradient(135deg, #7c3aed, #2563eb)',
                    color:'#fff', display:'flex', alignItems:'center', justifyContent:'center',
                    fontSize: 12, fontWeight: 700, letterSpacing:'0.04em',
                  }}>
                    {!u.image_url && initials(u.full_name || u.username)}
                  </div>
                  <span style={{
                    position:'absolute', right: -2, bottom: -2, width: 10, height: 10, borderRadius: '50%',
                    background: pulseColor, border:'2px solid var(--bg-card)',
                  }} title={ago(sec)} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color:'var(--text-primary)', display:'flex', alignItems:'center', gap: 6 }}>
                    {u.full_name || u.username}
                    {isMe && <span style={{ fontSize: 10, padding:'1px 6px', borderRadius: 100, background:'rgba(59,130,246,0.15)', color:'#2563eb', fontWeight: 700 }}>SİZ</span>}
                  </div>
                  <div style={{ fontSize: 11, color:'var(--text-tertiary)', display:'flex', alignItems:'center', gap: 6, flexWrap:'wrap' }}>
                    <span>{u.email || u.username}</span>
                    {u.last_seen_page && (
                      <>
                        <span>·</span>
                        <span style={{ padding:'1px 7px', borderRadius: 100, background:'rgba(124,58,237,0.10)', color:'#7c3aed', fontWeight: 600, fontSize: 10, letterSpacing:'0.02em' }}>
                          📍 {pageTitle(u.last_seen_page)}
                        </span>
                      </>
                    )}
                  </div>
                </div>
                <div style={{ fontSize: 11, color:'var(--text-tertiary)', fontVariantNumeric:'tabular-nums' }}>{ago(sec)}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// BUGÜNKÜ SATIŞ WIDGET
// ─────────────────────────────────────────────────────────────────────────────
function TodaySalesWidget({ onNavigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/dashboard/sales-summary');
        if (r.data.success) setData(r.data.data);
      } catch (e) {
        setError(e.response?.data?.message || e.message);
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <WidgetCard title="Satış Özeti"><div style={{padding:16,color:'var(--text-tertiary)',fontSize:'0.85rem'}}>Yükleniyor...</div></WidgetCard>;
  if (error) return <WidgetCard title="Satış Özeti"><div style={{padding:16,color:'var(--danger)',fontSize:'0.82rem'}}>Hata: {error}</div></WidgetCard>;
  if (!data) return null;

  const { today, yesterday, this_week, this_month, trend } = data;
  const diff = today.total - yesterday.total;
  const diffPct = yesterday.total > 0 ? (diff / yesterday.total) * 100 : null;
  const diffPositive = diff >= 0;

  return (
    <div style={{
      background: 'var(--bg-card)',
      border: '1px solid var(--border-secondary)',
      borderRadius: 12,
      padding: '16px 18px',
      marginBottom: 12,
      boxShadow: 'var(--shadow-xs)',
    }}>
      {/* Başlık */}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12,flexWrap:'wrap',gap:8}}>
        <div>
          <h3 style={{margin:0,fontSize:'0.85rem',fontWeight:600,color:'var(--text-secondary)',textTransform:'uppercase',letterSpacing:'0.04em'}}>📊 Satış Özeti</h3>
          <div style={{fontSize:'0.72rem',color:'var(--text-tertiary)',marginTop:2}}>{new Date().toLocaleDateString('tr-TR',{day:'2-digit',month:'long',year:'numeric'})}</div>
        </div>
        {onNavigate && (
          <button onClick={() => onNavigate('sales')} className="btn-ghost" style={{fontSize:'0.78rem',padding:'4px 10px'}}>Tüm Faturalar →</button>
        )}
      </div>

      {/* Ana metrik: Bugünkü satış (büyük) */}
      <div style={{display:'flex',alignItems:'flex-end',gap:16,marginBottom:14,flexWrap:'wrap'}}>
        <div>
          <div style={{fontSize:'0.7rem',color:'var(--text-tertiary)',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.04em',marginBottom:2}}>Bugün</div>
          <div style={{fontSize:'1.9rem',fontWeight:700,color:'var(--accent-primary)',lineHeight:1.1,fontFamily:'JetBrains Mono, monospace'}}>
            {fmtAmount(today.total)} <span style={{fontSize:'0.95rem',color:'var(--text-secondary)',fontWeight:500}}>TRY</span>
          </div>
          <div style={{fontSize:'0.78rem',color:'var(--text-tertiary)',marginTop:2}}>{today.count} fatura</div>
        </div>
        {/* Dünle karşılaştırma */}
        {yesterday.total > 0 && (
          <div style={{
            display:'flex',alignItems:'center',gap:6,
            padding:'6px 10px',
            borderRadius:8,
            background: diffPositive ? 'rgba(16,185,129,0.1)' : 'rgba(220,38,38,0.1)',
            color: diffPositive ? 'var(--success)' : 'var(--danger)',
            fontSize:'0.85rem',fontWeight:600,
          }}>
            {diffPositive ? '▲' : '▼'} {diffPct != null ? Math.abs(diffPct).toFixed(1) + '%' : '—'}
            <span style={{fontWeight:500,color:'var(--text-secondary)',fontSize:'0.78rem'}}>(düne göre)</span>
          </div>
        )}
      </div>

      {/* Alt metrikler: Dün / Bu Hafta / Bu Ay */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))',gap:10,marginBottom:14}}>
        <MiniMetric label="Dün" total={yesterday.total} count={yesterday.count} />
        <MiniMetric label="Bu Hafta" total={this_week.total} count={this_week.count} />
        <MiniMetric label="Bu Ay" total={this_month.total} count={this_month.count} />
      </div>

      {/* Son 30 gün mini grafik */}
      {trend && trend.length > 0 && (
        <div>
          <div style={{fontSize:'0.7rem',color:'var(--text-tertiary)',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.04em',marginBottom:6}}>Son 30 Gün</div>
          <SparklineChart data={trend} />
        </div>
      )}
    </div>
  );
}

function MiniMetric({ label, total, count }) {
  return (
    <div style={{
      padding:'8px 12px',
      background:'var(--bg-secondary)',
      borderRadius:8,
      border:'1px solid var(--border-secondary)',
    }}>
      <div style={{fontSize:'0.68rem',color:'var(--text-tertiary)',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.04em',marginBottom:2}}>{label}</div>
      <div style={{fontSize:'1rem',fontWeight:700,fontFamily:'JetBrains Mono, monospace'}}>{fmtAmount(total)} <span style={{fontSize:'0.7rem',color:'var(--text-tertiary)',fontWeight:500}}>TRY</span></div>
      <div style={{fontSize:'0.7rem',color:'var(--text-tertiary)'}}>{count} fatura</div>
    </div>
  );
}

function SparklineChart({ data }) {
  // Son 30 günü dolduralım: eksik günler 0
  const days = [];
  const today = new Date();
  for (let i = 29; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    const ds = d.toISOString().substring(0, 10);
    const found = data.find(t => t.date === ds);
    days.push({ date: ds, total: found ? found.total : 0, count: found ? found.count : 0 });
  }
  const max = Math.max(...days.map(d => d.total), 1);
  const W = 100, H = 50;
  const step = W / (days.length - 1 || 1);
  const points = days.map((d, i) => `${i * step},${H - (d.total / max) * (H - 4) - 2}`).join(' ');

  return (
    <div style={{position:'relative',background:'var(--bg-secondary)',borderRadius:8,padding:'10px 12px',border:'1px solid var(--border-secondary)'}}>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{width:'100%',height:60,display:'block'}}>
        <polyline points={points} fill="none" stroke="var(--accent-primary)" strokeWidth="1.5" vectorEffect="non-scaling-stroke"/>
        {/* Bugünkü nokta */}
        <circle cx={(days.length-1)*step} cy={H - (days[days.length-1].total / max) * (H - 4) - 2} r="2" fill="var(--accent-primary)"/>
      </svg>
      <div style={{display:'flex',justifyContent:'space-between',fontSize:'0.65rem',color:'var(--text-tertiary)',marginTop:4}}>
        <span>{days[0].date.substring(5)}</span>
        <span>Bugün</span>
      </div>
    </div>
  );
}

function WidgetCard({ title, children }) {
  return (
    <div style={{background:'var(--bg-card)',border:'1px solid var(--border-secondary)',borderRadius:12,padding:'14px 18px',marginBottom:12}}>
      <h3 style={{margin:'0 0 10px',fontSize:'0.85rem',fontWeight:600,color:'var(--text-secondary)',textTransform:'uppercase',letterSpacing:'0.04em'}}>{title}</h3>
      {children}
    </div>
  );
}

export default DashboardContent;
